/**
 * AI CONTEXT: service - Push Notification Service for Tolbert Tactical
 * Journal: J-20250103-01
 * 
 * Purpose:
 * - Manage push notification consent and preferences
 * - Handle device token registration and management
 * - Integrate with AWS Pinpoint for push notifications
 * - Provide unified push notification API for frontend
 * 
 * Features:
 * - Web push notification support
 * - Mobile push notification support (FCM/APNs)
 * - User consent management
 * - Device token management
 * - Integration with existing notification system
 */

import { getCurrentUser } from 'aws-amplify/auth';
import { authorizedFetch } from './apiClient';

class PushNotificationService {
  constructor() {
    this.isSupported = 'Notification' in window && 'serviceWorker' in navigator;
    this.permission = this.isSupported ? Notification.permission : 'denied';
    this.registration = null;
    this.vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    
    // Initialize service worker
    this.initializeServiceWorker();
  }

  /**
   * Initialize service worker for push notifications
   */
  async initializeServiceWorker() {
    if (!this.isSupported) {
      console.log('[push] Push notifications not supported');
      return;
    }

    try {
      this.registration = await navigator.serviceWorker.register('/sw.js');
      console.log('[push] Service worker registered:', this.registration);
      
      // Check for updates
      this.registration.addEventListener('updatefound', () => {
        const newWorker = this.registration.installing;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('[push] New service worker available');
            // Optionally show update notification to user
          }
        });
      });
    } catch (error) {
      console.error('[push] Service worker registration failed:', error);
    }
  }

  /**
   * Request push notification permission
   */
  async requestPermission() {
    if (!this.isSupported) {
      throw new Error('Push notifications not supported');
    }

    if (this.permission === 'granted') {
      return true;
    }

    if (this.permission === 'denied') {
      throw new Error('Push notifications are blocked. Please enable them in your browser settings.');
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      
      if (permission === 'granted') {
        console.log('[push] Push notification permission granted');
        return true;
      } else {
        console.log('[push] Push notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('[push] Error requesting permission:', error);
      throw error;
    }
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe() {
    if (!this.isSupported || !this.registration) {
      throw new Error('Push notifications not supported or service worker not registered');
    }

    try {
      // Request permission first
      const hasPermission = await this.requestPermission();
      if (!hasPermission) {
        throw new Error('Permission denied');
      }

      // Get push subscription
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
      });

      console.log('[push] Push subscription created:', subscription);

      // Register subscription with backend
      await this.registerSubscription(subscription);

      return subscription;
    } catch (error) {
      console.error('[push] Error subscribing to push notifications:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    if (!this.isSupported || !this.registration) {
      return false;
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        console.log('[push] Push subscription removed');
        
        // Unregister from backend
        await this.unregisterSubscription(subscription);
        return true;
      }
      return false;
    } catch (error) {
      console.error('[push] Error unsubscribing from push notifications:', error);
      return false;
    }
  }

  /**
   * Register push subscription with backend
   */
  async registerSubscription(subscription) {
    try {
      const user = await getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const subscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: this.arrayBufferToBase64(subscription.getKey('auth'))
        }
      };

      const response = await authorizedFetch('/push/register', {
        method: 'POST',
        body: JSON.stringify({
          userId: user.signInDetails?.loginId || user.username,
          subscription: subscriptionData,
            channelType: 'PUSH',
          deviceInfo: {
            platform: 'web',
            userAgent: navigator.userAgent,
            language: navigator.language
          }
        })
      });

      console.log('[push] Subscription registered with backend:', response);
      return response;
    } catch (error) {
      console.error('[push] Error registering subscription:', error);
      throw error;
    }
  }

  /**
   * Unregister push subscription from backend
   */
  async unregisterSubscription(subscription) {
    try {
      const user = await getCurrentUser();
      if (!user) {
        return;
      }

      await authorizedFetch('/push/unregister', {
        method: 'DELETE',
        body: JSON.stringify({
          userId: user.signInDetails?.loginId || user.username,
          endpoint: subscription.endpoint
        })
      });

      console.log('[push] Subscription unregistered from backend');
    } catch (error) {
      console.error('[push] Error unregistering subscription:', error);
    }
  }

  /**
   * Update push notification preferences
   */
  async updatePreferences(preferences) {
    try {
      const user = await getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await authorizedFetch('/push/preferences', {
        method: 'PUT',
        body: JSON.stringify({
          userId: user.signInDetails?.loginId || user.username,
          preferences: preferences
        })
      });

      console.log('[push] Preferences updated:', response);
      return response;
    } catch (error) {
      console.error('[push] Error updating preferences:', error);
      throw error;
    }
  }

  /**
   * Get current push notification preferences
   */
  async getPreferences() {
    try {
      const user = await getCurrentUser();
      if (!user) {
        return null;
      }

      const response = await authorizedFetch(`/push/preferences/${user.signInDetails?.loginId || user.username}`, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('[push] Error getting preferences:', error);
      return null;
    }
  }

  /**
   * Check if push notifications are enabled
   */
  async isEnabled() {
    if (!this.isSupported) {
      return false;
    }

    try {
      const preferences = await this.getPreferences();
      return preferences?.optIn === true && this.permission === 'granted';
    } catch (error) {
      console.error('[push] Error checking if enabled:', error);
      return false;
    }
  }

  /**
   * Send test push notification
   */
  async sendTestNotification() {
    try {
      const user = await getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await authorizedFetch('/push/test', {
        method: 'POST',
        body: JSON.stringify({
          userId: user.signInDetails?.loginId || user.username,
          title: 'Test Notification',
          body: 'This is a test push notification from Tolbert Tactical!'
        })
      });

      console.log('[push] Test notification sent:', response);
      return response;
    } catch (error) {
      console.error('[push] Error sending test notification:', error);
      throw error;
    }
  }

  /**
   * Utility function to convert VAPID key
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Utility function to convert ArrayBuffer to base64
   */
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Handle push notification click
   */
  handleNotificationClick(event) {
    event.notification.close();

    // Handle different notification types
    const data = event.notification.data;
    if (data) {
      switch (data.type) {
        case 'giveaway':
          window.location.href = `/giveaways/${data.giveawayId}`;
          break;
        case 'order':
          window.location.href = `/orders/${data.orderId}`;
          break;
        case 'shipping':
          window.location.href = `/orders/${data.orderId}`;
          break;
        case 'cart':
          window.location.href = '/checkout';
          break;
        default:
          window.location.href = '/';
      }
    } else {
      window.location.href = '/';
    }
  }

  /**
   * Initialize push notification handling
   * Note: This method is not used as event handlers are in the service worker
   */
  initializeHandlers() {
    // Event handlers are implemented in the service worker (/public/sw.js)
    // This method is kept for potential future use
    console.log('[push] Event handlers are managed by service worker');
  }
}

// Create singleton instance
const pushService = new PushNotificationService();

export default pushService;
